import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bot, User, TrendingUp, TrendingDown, Minus, ArrowRight } from "lucide-react";
import { Message, MessageType, ChartData, CardData, TableData } from "../types";
import { CrewAgent } from "@/types/crew-types";
import { format } from "date-fns";
import "./MessageComponent.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line, Bar, Pie, Doughnut } from "react-chartjs-2";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MessageComponentProps {
  message: Message;
  agent: CrewAgent;
}

const MessageComponent = ({ message, agent }: MessageComponentProps) => {
  const isUser = message.role === "user";
  const isSystem = message.role === "system";

  const renderContent = () => {
    switch (message.type) {
      case MessageType.TEXT:
        // Check if content contains HTML
        if (message.content.includes('<') && message.content.includes('>')) {
          return (
            <div 
              className="text-sm message-content"
              dangerouslySetInnerHTML={{ __html: message.content }}
            />
          );
        }
        return <p className="text-sm whitespace-pre-wrap">{message.content}</p>;

      case MessageType.CHART:
        const chartData = message.metadata as ChartData;
        return (
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-base">{chartData.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {chartData.type === "line" && <Line data={chartData.data} options={chartData.options} />}
                {chartData.type === "bar" && <Bar data={chartData.data} options={chartData.options} />}
                {chartData.type === "pie" && <Pie data={chartData.data} options={chartData.options} />}
                {chartData.type === "donut" && <Doughnut data={chartData.data} options={chartData.options} />}
              </div>
            </CardContent>
          </Card>
        );

      case MessageType.CARD:
        const cardData = message.metadata as CardData;
        return (
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-base">{cardData.title}</CardTitle>
              {cardData.description && (
                <CardDescription>{cardData.description}</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {cardData.image && (
                <img
                  src={cardData.image}
                  alt={cardData.title}
                  className="w-full h-32 object-cover rounded-md mb-4"
                />
              )}
              {cardData.stats && (
                <div className="grid grid-cols-2 gap-4">
                  {cardData.stats.map((stat, idx) => (
                    <div key={idx} className="space-y-1">
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-semibold">{stat.value}</p>
                        {stat.trend && (
                          <>
                            {stat.trend === "up" && <TrendingUp className="h-4 w-4 text-green-500" />}
                            {stat.trend === "down" && <TrendingDown className="h-4 w-4 text-red-500" />}
                            {stat.trend === "neutral" && <Minus className="h-4 w-4 text-gray-500" />}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {cardData.actions && (
                <div className="flex gap-2 mt-4">
                  {cardData.actions.map((action) => (
                    <Button key={action.id} variant="outline" size="sm">
                      {action.label}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );

      case MessageType.LIST:
        const items = message.metadata as string[];
        if (!items || !Array.isArray(items)) {
          return (
            <div className="text-sm text-muted-foreground">
              No items to display
            </div>
          );
        }
        return (
          <ul className="space-y-1">
            {items.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-muted-foreground">•</span>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        );

      case MessageType.TABLE:
        const tableData = message.metadata as TableData;
        if (!tableData || !tableData.headers || !tableData.rows) {
          return (
            <div className="text-sm text-muted-foreground">
              Invalid table data
            </div>
          );
        }
        return (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              {tableData.caption && (
                <caption className="text-sm text-muted-foreground mb-2">
                  {tableData.caption}
                </caption>
              )}
              <thead>
                <tr>
                  {tableData.headers.map((header, idx) => (
                    <th
                      key={idx}
                      className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableData.rows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {Array.isArray(row) ? row.map((cell, cellIdx) => (
                      <td key={cellIdx} className="px-4 py-2 text-sm">
                        {cell}
                      </td>
                    )) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case MessageType.BUTTON:
        return (
          <div className="flex flex-wrap gap-2">
            {message.actions?.map((action) => (
              <Button key={action.id} variant="outline" size="sm">
                {action.label}
              </Button>
            ))}
          </div>
        );

      case MessageType.SUGGESTION:
        return (
          <Card className="w-full max-w-md bg-primary/5 border-primary/20">
            <CardContent className="pt-4">
              <p className="text-sm font-medium mb-2">Suggested actions:</p>
              <div className="space-y-2">
                {message.actions?.map((action) => (
                  <Button
                    key={action.id}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    {action.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case MessageType.HANDOFF:
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline">System</Badge>
            <span>{message.content}</span>
          </div>
        );

      case MessageType.ERROR:
        return (
          <Card className="w-full max-w-md border-destructive/50 bg-destructive/5">
            <CardContent className="pt-4">
              <p className="text-sm text-destructive">{message.content}</p>
            </CardContent>
          </Card>
        );

      default:
        // Check if content contains HTML for default case too
        if (message.content.includes('<') && message.content.includes('>')) {
          return (
            <div 
              className="text-sm message-content"
              dangerouslySetInnerHTML={{ __html: message.content }}
            />
          );
        }
        return <p className="text-sm">{message.content}</p>;
    }
  };

  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        {renderContent()}
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : ""}`}>
      {!isUser && (
        <Avatar className="h-8 w-8">
          <AvatarImage src={agent.imagePath} />
          <AvatarFallback>
            <Bot className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      )}

      <div className={`max-w-[70%] ${isUser ? "bg-primary text-primary-foreground" : "bg-muted"} rounded-lg px-4 py-2`}>
        <div className="flex items-baseline justify-between mb-1">
          <span className={`text-xs font-medium ${isUser ? "text-primary-foreground" : "text-foreground"}`}>
            {isUser ? "You" : agent.name}
          </span>
          <span className={`text-xs ml-2 ${isUser ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
            {format(new Date(message.timestamp), "h:mm a")}
          </span>
        </div>
        {renderContent()}
      </div>

      {isUser && (
        <Avatar className="h-8 w-8">
          <AvatarFallback>
            <User className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};

export default MessageComponent;